import { useMemo } from 'react';
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { BillingAPI } from 'api/billing';
import {
  AddMembersRequestPayload,
  AddMembersResponse,
  BillingVersion,
  ChangePlanEstimateResponse,
  ChangePlanParams,
  ChangePlanRequestPayload,
  ChangePlanResponse,
  InvoiceDetails,
  InvoiceListResponse,
  Plans,
  PortalSession,
  Subscription,
} from 'api/billing/types';
import {
  getBillingVersion,
  getCurrentBillingCycle,
  getPlanId,
} from 'components/organisms/module-settings/billing/utils';
import { useBillingFeatureFlag } from 'hooks/use-account';
import { TranslationKey, useTranslate } from 'hooks/use-translation';

const MODULE = 'rl_billing';

export const SUBSCRIPTION_KEY = `${MODULE}_subscription`;
export const INVOICE_DETAILS_KEY = `${MODULE}_invoice_details`;
const INVOICE_LIST_KEY = `${MODULE}_invoice_list`;

export const useSubscriptionQuery = (
  queryOptions: UseQueryOptions<
    Subscription,
    unknown,
    Subscription,
    typeof SUBSCRIPTION_KEY
  > = {},
): UseQueryResult<Subscription> => {
  const isBillingEnabled = useBillingFeatureFlag();

  return useQuery(SUBSCRIPTION_KEY, () => BillingAPI.getSubscription(), {
    ...queryOptions,
    enabled: isBillingEnabled,
  });
};

export const useInvoiceDetailsQuery = (
  queryOptions: UseQueryOptions<
    InvoiceDetails,
    unknown,
    InvoiceDetails,
    typeof INVOICE_DETAILS_KEY
  > = {},
): UseQueryResult<InvoiceDetails> =>
  useQuery(INVOICE_DETAILS_KEY, () => BillingAPI.getInvoiceDetails(), {
    ...queryOptions,
  });

export const useInvoiceListQuery = (
  subscriptionId: number,
  queryOptions: UseQueryOptions<
    InvoiceListResponse,
    unknown,
    InvoiceListResponse,
    typeof INVOICE_LIST_KEY
  > = {},
): UseQueryResult<InvoiceListResponse> =>
  useQuery(INVOICE_LIST_KEY, () => BillingAPI.getInvoiceList(subscriptionId), {
    ...queryOptions,
  });

const getChangePlanRequestPayload = ({
  plan,
  form,
  subscription,
}: ChangePlanParams): ChangePlanRequestPayload => {
  const planId = getPlanId(
    plan,
    form?.billing_cycle || getCurrentBillingCycle(subscription.plan.planName),
  );

  const payload: ChangePlanRequestPayload = {
    plan: {
      planId,
      quantity: form?.quantity || subscription.licenseQuantity,
    },
    subscriptionId: subscription.subscriptionId,
  };

  return payload;
};

const changePlanMutationFn = (changePlanParams) => {
  return BillingAPI.changePlan(getChangePlanRequestPayload(changePlanParams));
};

const downgradePlanMutationFn = (changePlanParams) => {
  return BillingAPI.changePlan({
    ...getChangePlanRequestPayload(changePlanParams),
    hasScheduledChanges: true,
  });
};

const changePlanCheckoutMutationFn = ({
  plan,
  form,
  subscription,
  isReactivation,
}: ChangePlanParams) => {
  const planId = getPlanId(
    plan,
    form?.billing_cycle || getCurrentBillingCycle(subscription.plan.planName),
  );

  const payload = {
    plan: {
      planId,
      quantity: form?.quantity || subscription.licenseQuantity,
    },
    subscriptionId: subscription.subscriptionId,
  };

  return isReactivation
    ? BillingAPI.reactivationCheckout(payload)
    : BillingAPI.changePlanCheckout(payload);
};

const changePlanEstimateMutationFn = ({
  plan,
  form,
  subscription,
}: ChangePlanParams) => {
  const planId = getPlanId(
    plan,
    form?.billing_cycle || getCurrentBillingCycle(subscription.plan.planName),
  );

  const payload = {
    plan: {
      planId,
      quantity: form?.quantity || subscription.licenseQuantity,
    },
    subscriptionId: subscription.subscriptionId,
  };

  return BillingAPI.changePlanEstimate(payload);
};

const updateInvoiceDetailsMutationFn = (payload: Partial<InvoiceDetails>) => {
  return BillingAPI.updateInvoiceDetails(payload);
};

export const useChangePlanMutation = (
  options: UseMutationOptions<
    ChangePlanResponse,
    unknown,
    ChangePlanParams
  > = {},
) => useMutation(changePlanMutationFn, options);

export const useDowngradePlanMutation = (
  options: UseMutationOptions<
    ChangePlanResponse,
    unknown,
    ChangePlanParams
  > = {},
) => useMutation(downgradePlanMutationFn, options);

export const useChangePlanEstimateMutation = (
  options: UseMutationOptions<
    ChangePlanEstimateResponse,
    unknown,
    ChangePlanParams
  > = {},
) => useMutation(changePlanEstimateMutationFn, options);

export const useChangePlanCheckoutMutation = (
  options: UseMutationOptions<
    ChangePlanResponse,
    unknown,
    ChangePlanParams
  > = {},
) => useMutation(changePlanCheckoutMutationFn, options);

export const useCreatePortalSessionMutation = (
  options: UseMutationOptions<PortalSession, unknown, void> = {},
) => useMutation(() => BillingAPI.createPortalSession(), options);

export const useRemoveScheduledPlanChangeMutation = (
  options: UseMutationOptions<void, unknown, void> = {},
) => useMutation(() => BillingAPI.removeScheduledPlanChange(), options);

export const useScheduleSubscriptionCancellationMutation = (
  options: UseMutationOptions<void, unknown, void> = {},
) =>
  useMutation(
    (formValues) => BillingAPI.scheduleSubscriptionCancellation(formValues),
    options,
  );

export const useRemoveScheduledCancellationMutation = (
  options: UseMutationOptions<void, unknown, void> = {},
) => useMutation(() => BillingAPI.removeScheduledCancellation(), options);

export const useUpdateInvoiceDetailsMutation = (
  options: UseMutationOptions<
    InvoiceDetails,
    unknown,
    Partial<InvoiceDetails>
  > = {},
) => useMutation(updateInvoiceDetailsMutationFn, options);

const addLicensesMutationFn = (count: number) => {
  return BillingAPI.addLicences(count);
};

const addLicensesEstimateMutationFn = (count: number) => {
  return BillingAPI.addLicencesEstimate(count);
};

export const useAddLicensesMutation = (
  options: UseMutationOptions<Subscription, unknown, number> = {},
) => useMutation(addLicensesMutationFn, options);

export const useAddLicensesEstimateMutation = (
  options: UseMutationOptions<ChangePlanEstimateResponse, unknown, number> = {},
) => useMutation(addLicensesEstimateMutationFn, options);

const forceSyncSubscriptionMutationFn = (subscriptionId: number) => {
  return BillingAPI.forceSyncSubscription(subscriptionId);
};

export const useForceSyncSubscriptionMutation = (
  options: UseMutationOptions<void, unknown, number> = {},
) => useMutation(forceSyncSubscriptionMutationFn, options);

const addMembersMutationFn = (payload: AddMembersRequestPayload) => {
  return BillingAPI.addMembers(payload);
};

export const useAddMembersMutation = (
  options: UseMutationOptions<
    AddMembersResponse,
    unknown,
    AddMembersRequestPayload
  > = {},
) => useMutation(addMembersMutationFn, options);

const addMembersEstimateMutationFn = (payload: AddMembersRequestPayload) => {
  return BillingAPI.addMembersEstimate(payload);
};

export type MembersEstimateMutationType = (
  options?: UseMutationOptions<
    ChangePlanEstimateResponse,
    unknown,
    AddMembersRequestPayload
  >,
) => UseMutationResult<
  ChangePlanEstimateResponse,
  unknown,
  AddMembersRequestPayload
>;

export const useAddMembersEstimateMutation = (
  options: UseMutationOptions<
    ChangePlanEstimateResponse,
    unknown,
    AddMembersRequestPayload
  > = {},
): UseMutationResult<
  ChangePlanEstimateResponse,
  unknown,
  AddMembersRequestPayload
> => useMutation(addMembersEstimateMutationFn, options);

export const useAddPartnerMembersEstimateMutation = (
  options: UseMutationOptions<
    ChangePlanEstimateResponse,
    unknown,
    AddMembersRequestPayload
  > = {},
): UseMutationResult<
  ChangePlanEstimateResponse,
  unknown,
  AddMembersRequestPayload
> =>
  useMutation(
    (payload: AddMembersRequestPayload) =>
      BillingAPI.addPartnerMembersEstimate(payload),
    options,
  );

export type ReactivateMemberMutationType = (
  options?: UseMutationOptions<ChangePlanEstimateResponse, unknown, number>,
) => UseMutationResult<ChangePlanEstimateResponse, unknown, number>;
const reactivateMemberMutationFn = (userId: number) => {
  return BillingAPI.reactivateMember(userId);
};

export const useReactivateMemberMutation = (
  options: UseMutationOptions<unknown, unknown, number> = {},
) => useMutation(reactivateMemberMutationFn, options);

const reactivateMemberEstimateMutationFn = (userId: number) => {
  return BillingAPI.reactivateMemberEstimate(userId);
};

export const useReactivateMemberEstimateMutation = (
  options: UseMutationOptions<ChangePlanEstimateResponse, unknown, number> = {},
): UseMutationResult<ChangePlanEstimateResponse, unknown, number> =>
  useMutation(reactivateMemberEstimateMutationFn, options);
export const useReactivatePartnerMemberEstimateMutation = (
  options: UseMutationOptions<ChangePlanEstimateResponse, unknown, number> = {},
): UseMutationResult<ChangePlanEstimateResponse, unknown, number> =>
  useMutation(
    (userId) => BillingAPI.reactivatePartnerMemberEstimate(userId),
    options,
  );

export const useAvailableLicensesQuery = () => {
  const { data: subscription } = useSubscriptionQuery();
  const availableLicenses = useMemo(
    () =>
      subscription
        ? subscription?.licenseQuantity - subscription?.usedQuantity.LICENSE
        : 0,
    [subscription],
  );

  return availableLicenses;
};

export const useBillingVersion = (): BillingVersion | null => {
  const { data: subscription } = useSubscriptionQuery();
  const version = useMemo(
    () => (subscription ? getBillingVersion(subscription?.plan.planId) : null),
    [subscription],
  );

  return version;
};

export const usePlanName = (plan?: Plans | null): string => {
  const translate = useTranslate();

  return useMemo(() => {
    if (!plan) {
      return '';
    }
    const planKey = `billing.plan.${plan}`.toLowerCase();

    return translate(planKey as TranslationKey);
  }, [plan, translate]);
};
